import React from 'react';
import type { PlantafelCreateDraft, PlantafelViewMode } from './plantafelTypes';
import { usePlantafelDisplay } from './PlantafelTimelineContext';
import {
  clientXToPct,
  formatBlockScheduleLabel,
  pctSpanToBlockHours,
  spanToIsoRange,
} from './plantafelBlockTime';
import type { PlantafelDayHours, PlantafelVisibleWeekdays } from '../../utils/plantafelSettings';
import { plantafelHourSpan } from '../../utils/plantafelSettings';
import { visibleColumnCount, visibleColumnToAbsoluteDay, weekDayWidthPct } from '../../utils/plantafelVisibleDays';

const MIN_DRAG_PCT = 0.35;

function dayWidthPctForView(
  viewMode: 'tag' | 'woche' | 'monat',
  visibleWeekdays: PlantafelVisibleWeekdays,
  monthDayCount?: number,
): number {
  if (viewMode === 'tag') return 100;
  if (viewMode === 'monat') {
    const weekCount = Math.max(1, Math.ceil((monthDayCount ?? 35) / 7));
    return 100 / Math.max(visibleColumnCount(visibleWeekdays, weekCount), 1);
  }
  return weekDayWidthPct(visibleWeekdays);
}

function createDraftAtTimelinePct(
  leftPct: number,
  durationHours: number,
  timeView: 'tag' | 'woche' | 'monat',
  viewportStart: Date,
  visibleWeekdays: PlantafelVisibleWeekdays,
  dayHours: PlantafelDayHours,
  timeZone: string,
  employeeId?: string,
  leadId?: string | null,
  monthDayCount?: number,
): PlantafelCreateDraft {
  let span: ReturnType<typeof pctSpanToBlockHours>;
  if (timeView === 'monat') {
    // Month day columns are too narrow for reliable hour picking from X —
    // place a default-duration appointment at the working-day start.
    const dayWidth = dayWidthPctForView(timeView, visibleWeekdays, monthDayCount);
    const weekCount = Math.max(1, Math.ceil((monthDayCount ?? 35) / 7));
    const colCount = Math.max(visibleColumnCount(visibleWeekdays, weekCount), 1);
    const col = Math.min(Math.max(0, Math.floor(leftPct / dayWidth)), colCount - 1);
    const maxDay = Math.max(0, (monthDayCount ?? 35) - 1);
    const dayStart = Math.min(maxDay, visibleColumnToAbsoluteDay(col, visibleWeekdays));
    const hourStart = dayHours.hourStart;
    const hourEnd = Math.min(dayHours.hourEnd, hourStart + durationHours);
    span = { dayStart, dayEnd: dayStart, hourStart, hourEnd };
  } else {
    const dayWidth = dayWidthPctForView(timeView, visibleWeekdays, monthDayCount);
    const hourSpan = plantafelHourSpan(dayHours);
    const widthPct = (durationHours / hourSpan) * dayWidth;
    span = pctSpanToBlockHours(
      leftPct,
      widthPct,
      timeView,
      visibleWeekdays,
      dayHours,
      monthDayCount,
    );
  }
  const { due_at, end_at } = spanToIsoRange(viewportStart, span, timeZone);
  return {
    due_at,
    end_at,
    lead_id: leadId ?? null,
    assignee_employee_ids: employeeId ? [employeeId] : [],
  };
}

type DragState = {
  rect: DOMRect;
  anchorLeftPct: number;
  /** Widest span seen during this gesture (for cancel-vs-click detection). */
  maxWidthPct: number;
};

export function usePlantafelCreateDrag({
  enabled,
  viewMode,
  viewportStart,
  timelineRef,
  employeeId,
  leadId,
  monthDayCount,
  onComplete,
  timeZone,
}: {
  enabled: boolean;
  viewMode: PlantafelViewMode;
  viewportStart: Date;
  timelineRef: React.RefObject<HTMLDivElement | null>;
  employeeId?: string;
  leadId?: string | null;
  monthDayCount?: number;
  onComplete?: (draft: PlantafelCreateDraft) => void;
  timeZone: string;
}) {
  const { visibleWeekdays, dayHours, defaultActivityDurationMinutes } = usePlantafelDisplay();
  const [previewPct, setPreviewPct] = React.useState<{ leftPct: number; widthPct: number } | null>(
    null,
  );
  const [previewLabel, setPreviewLabel] = React.useState<string | null>(null);
  const dragRef = React.useRef<DragState | null>(null);
  const onCompleteRef = React.useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Keep the latest display settings reachable from the document-level pointer
  // handlers, which are attached once per drag and would otherwise capture a
  // stale snapshot if the user changes settings mid-drag.
  const displayRef = React.useRef({
    visibleWeekdays,
    dayHours,
    monthDayCount,
    defaultActivityDurationMinutes,
  });
  displayRef.current = { visibleWeekdays, dayHours, monthDayCount, defaultActivityDurationMinutes };

  const timeView =
    viewMode === 'tag' || viewMode === 'woche' || viewMode === 'monat' ? viewMode : null;

  const reset = React.useCallback(() => {
    dragRef.current = null;
    setPreviewPct(null);
    setPreviewLabel(null);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!enabled || !timeView || !onCompleteRef.current || e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-activity-block]')) return;
    const el = timelineRef.current;
    if (!el || !el.contains(target)) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = el.getBoundingClientRect();
    const xPct = clientXToPct(e.clientX, rect);
    dragRef.current = { rect, anchorLeftPct: xPct, maxWidthPct: 0 };
    setPreviewPct({ leftPct: xPct, widthPct: 0 });
    setPreviewLabel(null);

    const update = (clientX: number) => {
      const drag = dragRef.current;
      if (!drag || !timeView) return;
      const pct = clientXToPct(clientX, drag.rect);
      const leftPct = Math.min(drag.anchorLeftPct, pct);
      const widthPct = Math.max(Math.abs(pct - drag.anchorLeftPct), 0);
      drag.maxWidthPct = Math.max(drag.maxWidthPct, widthPct);
      setPreviewPct({ leftPct, widthPct });
      if (widthPct >= MIN_DRAG_PCT) {
        const { visibleWeekdays: vw, dayHours: dh, monthDayCount: md } = displayRef.current;
        const span = pctSpanToBlockHours(leftPct, widthPct, timeView, vw, dh, md);
        setPreviewLabel(formatBlockScheduleLabel(viewportStart, span, timeZone));
      } else {
        setPreviewLabel(null);
      }
    };

    const finish = (clientX: number) => {
      const drag = dragRef.current;
      const complete = onCompleteRef.current;
      if (drag && timeView && complete) {
        const pct = clientXToPct(clientX, drag.rect);
        const leftPct = Math.min(drag.anchorLeftPct, pct);
        const widthPct = Math.max(Math.abs(pct - drag.anchorLeftPct), 0);
        const maxWidthPct = Math.max(drag.maxWidthPct, widthPct);
        const {
          visibleWeekdays: vw,
          dayHours: dh,
          monthDayCount: md,
          defaultActivityDurationMinutes: durationMinutes,
        } = displayRef.current;
        if (widthPct >= MIN_DRAG_PCT) {
          const span = pctSpanToBlockHours(leftPct, widthPct, timeView, vw, dh, md);
          const { due_at, end_at } = spanToIsoRange(viewportStart, span, timeZone);
          complete({
            due_at,
            end_at,
            lead_id: leadId ?? null,
            assignee_employee_ids: employeeId ? [employeeId] : [],
          });
        } else if (maxWidthPct < MIN_DRAG_PCT) {
          complete(
            createDraftAtTimelinePct(
              drag.anchorLeftPct,
              durationMinutes / 60,
              timeView,
              viewportStart,
              vw,
              dh,
              timeZone,
              employeeId,
              leadId,
              md,
            ),
          );
        }
      }
      reset();
      document.removeEventListener('pointermove', onDocMove);
      document.removeEventListener('pointerup', onDocUp);
      document.removeEventListener('pointercancel', onDocCancel);
    };

    const onDocMove = (ev: PointerEvent) => {
      ev.preventDefault();
      update(ev.clientX);
    };
    const onDocUp = (ev: PointerEvent) => finish(ev.clientX);
    const onDocCancel = () => {
      reset();
      document.removeEventListener('pointermove', onDocMove);
      document.removeEventListener('pointerup', onDocUp);
      document.removeEventListener('pointercancel', onDocCancel);
    };

    document.addEventListener('pointermove', onDocMove);
    document.addEventListener('pointerup', onDocUp);
    document.addEventListener('pointercancel', onDocCancel);
  };

  return {
    previewPct,
    previewLabel,
    onPointerDown,
  };
}
